
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// import { getAuth } from 'firebase/auth'; // Removed
import { useToast } from '@/hooks/use-toast';
import { useFirebase } from '@/context/FirebaseProvider'; // Added
import { useIsMobile } from '@/hooks/use-mobile';
import { syncPortfolioMembers } from '@/utils/firestore/users';
import { usePortfolioSettingsState } from '@/hooks/portfolioSettings/usePortfolioSettingsState';

// Import refactored components
import PortfolioSettingsContent from '@/components/portfolio/settings/PortfolioSettingsContent';
import PortfolioSettingsLoading from '@/components/portfolio/settings/PortfolioSettingsLoading';

// Import types
import { PortfolioSettingsStateType } from '@/components/portfolio/settings/types/portfolioSettingsTypes';

const PortfolioSettings = () => {
  const isMobile = useIsMobile();
  const { portfolioId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  // const auth = getAuth(); // Removed
  const { portfolios: allPortfoliosFromFirebase, firebaseUser, isLoading: firebaseIsLoading } = useFirebase(); // Use useFirebase
  const [isInitializing, setIsInitializing] = useState(true); // Will be primarily driven by firebaseIsLoading
  
  // Prevent multiple save operations
  const saveInProgressRef = useRef(false);
  
  // IMPORTANT: Always call usePortfolioSettingsState here, regardless of loading state
  // This ensures hooks are called in the same order on every render
  const portfolioSettingsState = usePortfolioSettingsState() as PortfolioSettingsStateType;
  
  // Ensure data consistency before rendering the main content
  useEffect(() => {
    // This effect now primarily handles the syncPortfolioMembers logic
    // and navigation if portfolioId is missing.
    // The main loading state is handled by firebaseIsLoading.
    if (firebaseIsLoading) {
      setIsInitializing(true); // Keep local initializing true while firebase is loading
      return;
    }
    // Once firebaseIsLoading is false, we can proceed
    setIsInitializing(false); 

    const ensureDataConsistency = async () => {
      if (!portfolioId) {
        console.error('No portfolio ID provided');
        toast({
          title: "Error",
          description: "No portfolio was selected. Redirecting to home page."
        });
        navigate('/');
        return;
      }
      
      try {
        console.log(`Ensuring data consistency for portfolio ${portfolioId}`);
        
        if (!firebaseUser) {
          console.warn('No authenticated user found when accessing portfolio settings');
        } else {
          console.log(`Authenticated user: ${firebaseUser.uid} (${firebaseUser.email})`);
        }
        
        await syncPortfolioMembers(portfolioId);
        console.log(`Data consistency check completed for portfolio ${portfolioId}`);
      } catch (error) {
        console.error('Error during data consistency check:', error);
        toast({
          title: "Warning",
          description: "There was an issue preparing the portfolio data."
        });
      }
      // No finally block to set isInitializing, it's based on firebaseIsLoading
    };
    
    ensureDataConsistency();
  }, [portfolioId, navigate, toast, firebaseUser, firebaseIsLoading]); // Added firebaseIsLoading
  
  // Show loading while initializing (driven by firebaseIsLoading via isInitializing state)
  if (isInitializing || firebaseIsLoading) { // Check both
    return <PortfolioSettingsLoading />;
  }
  
  // Get owned portfolios for the dropdown from FirebaseProvider
  const allPortfolios = allPortfoliosFromFirebase || []; // Use data from FirebaseProvider
  
  // Format the portfolios for the dropdown
  const portfolioOptions = allPortfolios
    .map(p => {
      const currentUserMember = p.members?.find(m => m.userId === firebaseUser?.uid);
      const userRoleInPortfolio = currentUserMember?.role || null;
      return {
        id: p.id, // p.id is string from Zod schema
        name: p.name, // p.name is string from Zod schema
        // title: p.title, // title is not in Zod schema, use name
        role: userRoleInPortfolio, // Role of the current user in this portfolio
        isOwnerOrPrimaryOwner: userRoleInPortfolio === 'Owner' || userRoleInPortfolio === 'Primary Owner'
      };
    })
    .filter(p => p.isOwnerOrPrimaryOwner) // Filter for portfolios where current user is Owner or Primary Owner
    .map(p => ({
      id: p.id as string, // Assert as string, though Zod schema should ensure it
      title: p.name || 'Unnamed Portfolio', // Use p.name, provide fallback
      role: p.role as string | null // Assert role as string | null
    }));

  // Add a throttling wrapper around handleSave to prevent multiple rapid saves
  const handleSaveWithThrottle = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (saveInProgressRef.current) {
      console.log("Save already in progress, throttling additional save requests");
      toast({
        title: "Already saving",
        description: "Please wait while your changes are being saved."
      });
      return;
    }
    
    saveInProgressRef.current = true;
    
    portfolioSettingsState.handleSave(e);
    
    // Reset the flag after a delay
    setTimeout(() => {
      saveInProgressRef.current = false;
    }, 5000);
  };

  return (
    <PortfolioSettingsContent 
      isMobile={isMobile}
      portfolioId={portfolioId || ''}
      portfolioOptions={portfolioOptions}
      saveInProgressRef={saveInProgressRef}
      portfolioSettingsState={portfolioSettingsState}
      handleSaveWithThrottle={handleSaveWithThrottle}
    />
  );
};

export default PortfolioSettings;
